import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mail, PenTool, Palette, BarChart3, Lightbulb, Share2 } from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

const services = [
  {
    icon: Mail,
    title: "Email Marketing",
    id: "email-marketing",
    description: "Hyper-personalized email marketing system that consistently outperforms industry standards across all metrics.",
    features: [
      "40% Higher Open Rates",
      "3x Industry CTR",
      "Advanced Segmentation",
      "Automated Workflows"
    ],
    highlight: true
  },
  {
    icon: PenTool,
    title: "Content Creation",
    id: "content-creation",
    description: "Engaging, conversion-focused content that tells your brand story and connects with your audience across all platforms.",
    features: [
      "Social Media Content",
      "Blog & Article Writing",
      "Video Production",
      "Visual Content Design"
    ],
    highlight: true
  },
  {
    icon: Palette,
    title: "Brand Design",
    id: "brand-design",
    description: "Create a distinctive visual identity that sets you apart from competitors and resonates with your target audience.",
    features: [
      "Logo & Visual Identity",
      "Brand Guidelines",
      "Marketing Materials",
      "Brand Strategy"
    ]
  },
  {
    icon: BarChart3,
    title: "Analytics & Reporting",
    id: "analytics-reporting",
    description: "Data-driven insights and comprehensive reporting to optimize your marketing performance and ROI.",
    features: [
      "Performance Tracking",
      "Competitor Analysis",
      "ROI Measurement",
      "Strategic Recommendations"
    ]
  },
  {
    icon: Lightbulb,
    title: "Creative Strategy",
    id: "creative-strategy",
    description: "Innovative creative approaches that capture attention and drive meaningful engagement with your brand.",
    features: [
      "Campaign Development",
      "Creative Direction",
      "Content Strategy",
      "Brand Storytelling"
    ]
  },
  {
    icon: Share2,
    title: "Social Strategy",
    id: "social-strategy",
    description: "Strategic social media management that builds community, drives engagement, and generates leads.",
    features: [
      "Platform Strategy",
      "Content Calendar",
      "Community Management",
      "Growth Tactics"
    ]
  }
];

export default function Services() {
  const navigate = useNavigate();

  const handleServiceClick = (serviceId: string) => {
    navigate('/contact', { state: { selectedService: serviceId } });
  };

  return (
    <div className="page-container">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="float-slow">
            Our <span className="text-golden">Services</span>
          </h1>
          <p className="text-xl text-gray-300 float-medium">
            Strategic solutions that drive real business growth
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => (
            <motion.button
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => handleServiceClick(service.id)}
              className={`text-left scroll-mt-20 float-${index % 2 === 0 ? 'slow' : 'medium'} w-full`}
            >
              <div
                className={`service-card ${
                  service.highlight 
                    ? 'bg-golden/10 border-golden hover:bg-golden/20' 
                    : 'bg-black/30 border-gray-800 hover:border-golden'
                } cursor-pointer`}
              >
                <service.icon className={`w-12 h-12 mb-6 ${service.highlight ? 'text-golden float-medium' : 'text-golden float-slow'}`} />
                <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-300 mb-6">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, i) => (
                    <li key={i} className={`flex items-center text-gray-300 float-${i % 2 === 0 ? 'slow' : 'medium'}`}>
                      <span className="text-golden mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
                {service.highlight && (
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-golden text-white text-sm font-medium rounded-full float-fast">
                      Industry Leading
                    </span>
                  </div>
                )}
              </div>
            </motion.button>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 md:p-12 border border-golden/30 text-center mb-16">
            <h2 className="text-3xl font-bold mb-4 float-slow">
              Ready to Transform Your Digital Presence?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto float-medium">
              Let's create a tailored strategy that aligns with your business goals and drives measurable results.
            </p>
            <button
              onClick={() => handleServiceClick('consultation')}
              className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold hover:bg-golden-600 transition-all duration-300 transform hover:scale-110 float-fast"
            >
              Schedule a Strategy Call
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}